import { useRef, useState, useCallback, useMemo } from 'react';
import { getModuleZIndex, computeSnapAndGuides, computeCollisions, wouldCollide, getModuleBBox, BODY_MODULE_TYPES } from '@/lib/furnitureData';
import { Trash2, ZoomIn, ZoomOut, Copy, Ruler, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ContextMenu from './ContextMenu';

const SNAP_SIZE = 5;
const HANDLE = 8;

export default function BuilderCanvas({
  modules, selectedId, scale, snapEnabled, showDimensions, enforceNoOverlap,
  onSelect, onUpdateModule, onAddModuleAtPosition, onDeleteModule, onCopyModule, onRotateModule, onScaleChange,
}) {
  const canvasRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dropActive, setDropActive] = useState(false);
  const [guides, setGuides] = useState([]);
  const [resize, setResize] = useState(null);
  const [ctx, setCtx] = useState(null);

  const collisions = useMemo(() => computeCollisions(modules), [modules]);

  const snapGrid = useCallback((v) => snapEnabled ? Math.round(v / SNAP_SIZE) * SNAP_SIZE : v, [snapEnabled]);

  const handleMouseDown = useCallback((e, moduleId) => {
    if (e.button === 2) return;
    e.stopPropagation();
    const mod = modules.find(m => m.id === moduleId);
    if (!mod) return;
    onSelect(moduleId);
    if (BODY_MODULE_TYPES.includes(mod.type)) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setDragging(moduleId);
    setDragOffset({
      x: e.clientX - rect.left - mod.x * scale,
      y: e.clientY - rect.top - mod.y * scale,
    });
  }, [modules, scale, onSelect]);

  const handleResizeStart = useCallback((e, moduleId, corner) => {
    e.stopPropagation();
    e.preventDefault();
    const mod = modules.find(m => m.id === moduleId);
    if (!mod) return;
    setResize({ moduleId, corner, startX: e.clientX, startY: e.clientY,
      origW: mod.width, origH: mod.height, origX: mod.x, origY: mod.y });
    onSelect(moduleId);
  }, [modules, onSelect]);

  const handleMouseMove = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();

    if (resize) {
      const mod = modules.find(m => m.id === resize.moduleId);
      if (!mod) return;
      const dx = (e.clientX - resize.startX) / scale;
      const dy = (e.clientY - resize.startY) / scale;
      let { origW, origH, origX, origY, corner } = resize;
      let newX = origX, newY = origY, newW = origW, newH = origH;
      if (corner.includes('e')) newW = Math.max(5, origW + dx);
      if (corner.includes('w')) { newW = Math.max(5, origW - dx); newX = origX + (origW - newW); }
      if (corner.includes('s')) newH = Math.max(5, origH + dy);
      if (corner.includes('n')) { newH = Math.max(5, origH - dy); newY = origY + (origH - newH); }
      newW = Math.round(snapGrid(newW));
      newH = Math.round(snapGrid(newH));
      newX = Math.round(snapGrid(newX));
      newY = Math.round(snapGrid(newY));
      onUpdateModule(resize.moduleId, { x: newX, y: newY, width: newW, height: newH });
      return;
    }

    if (!dragging) return;
    const mod = modules.find(m => m.id === dragging);
    if (!mod) return;
    const rawX = Math.max(0, (e.clientX - rect.left - dragOffset.x) / scale);
    const rawY = Math.max(0, (e.clientY - rect.top - dragOffset.y) / scale);
    const gridX = snapGrid(rawX);
    const gridY = snapGrid(rawY);
    const snapResult = computeSnapAndGuides(mod, modules, gridX, gridY, snapEnabled);
    setGuides(snapResult.guides);
    const nextX = Math.round(snapResult.x);
    const nextY = Math.round(snapResult.y);
    if (enforceNoOverlap && wouldCollide(dragging, { x: nextX, y: nextY }, modules)) {
      return;
    }
    onUpdateModule(dragging, { x: nextX, y: nextY });
  }, [dragging, resize, dragOffset, scale, onUpdateModule, snapEnabled, modules, enforceNoOverlap, snapGrid]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
    setResize(null);
    setGuides([]);
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropActive(true);
  };
  const handleDragLeave = () => setDropActive(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDropActive(false);
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const rect = canvasRef.current.getBoundingClientRect();
      const x = Math.round((e.clientX - rect.left) / scale);
      const y = Math.round((e.clientY - rect.top) / scale);
      onAddModuleAtPosition(data, x, y);
    } catch {}
  };

  const openCtx = (e, modId) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(modId);
    setCtx({ x: e.clientX, y: e.clientY, moduleId: modId });
  };

  // Canvas bounds for overlay sizing
  const { canvasW, canvasH } = useMemo(() => {
    let maxX = 400, maxY = 400;
    modules.forEach(m => {
      const b = getModuleBBox(m);
      maxX = Math.max(maxX, b.x2 + 40);
      maxY = Math.max(maxY, b.y2 + 40);
    });
    return { canvasW: maxX * scale, canvasH: maxY * scale };
  }, [modules, scale]);

  return (
    <div className="flex-1 flex flex-col bg-[#F4F1ED] overflow-hidden" data-testid="builder-canvas-wrapper">
      <div className="h-10 px-4 flex items-center justify-between border-b border-[#E0DDD8] bg-[#FAFAFA]">
        <span className="mono-text text-xs text-[#5C5C5C]">
          {modules.length} модул{modules.length === 1 ? 'ь' : modules.length < 5 ? 'я' : 'ей'}
          {collisions.size > 0 && (
            <span className="ml-2 text-red-500 font-semibold">
              · {collisions.size} конфликт{collisions.size === 1 ? '' : collisions.size < 5 ? 'а' : 'ов'}
            </span>
          )}
        </span>
        <div className="flex items-center gap-1">
          <div
            className="flex items-center gap-1 px-2 h-7 rounded-sm bg-[#C25934]/8 border border-[#C25934]/20"
            title="Размеры и запрет пересечений всегда включены"
          >
            <Ruler className="w-3 h-3 text-[#C25934]" />
            <ShieldAlert className="w-3 h-3 text-[#C25934]" />
            <span className="mono-text text-[10px] text-[#C25934]">АВТО</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onScaleChange(Math.max(1, scale - 0.5))}
            className="h-7 w-7 p-0" data-testid="zoom-out-btn">
            <ZoomOut className="w-3.5 h-3.5" />
          </Button>
          <span className="mono-text text-xs text-[#5C5C5C] w-12 text-center">
            {Math.round(scale * 100 / 2)}%
          </span>
          <Button variant="ghost" size="sm" onClick={() => onScaleChange(Math.min(4, scale + 0.5))}
            className="h-7 w-7 p-0" data-testid="zoom-in-btn">
            <ZoomIn className="w-3.5 h-3.5" />
          </Button>
          {selectedId && (
            <>
              <Button variant="ghost" size="sm" onClick={() => onCopyModule(selectedId)}
                className="h-7 w-7 p-0 text-[#5C5C5C] hover:text-[#C25934]" data-testid="copy-module-btn">
                <Copy className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDeleteModule(selectedId)}
                className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" data-testid="delete-module-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div
          ref={canvasRef}
          id="builder-canvas"
          className={`canvas-grid-bg relative min-h-[400px] min-w-[400px] rounded-sm border border-[#E0DDD8] bg-white ${dropActive ? 'drop-zone-active' : ''}`}
          style={{ width: Math.max(canvasW, 400), minWidth: '100%', minHeight: Math.max(canvasH, 400), height: 'auto' }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => onSelect(null)}
          onContextMenu={(e) => e.preventDefault()}
          data-testid="builder-canvas"
        >
          {modules.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-6">
              <div className="text-center max-w-sm mx-auto">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#C25934]/10 flex items-center justify-center mb-4">
                  <svg viewBox="0 0 24 24" className="w-8 h-8 text-[#C25934]" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" />
                  </svg>
                </div>
                <p className="text-base font-semibold text-[#1C1C1C]">Начните проект</p>
                <p className="text-xs text-[#5C5C5C] mt-2 leading-relaxed">
                  Слева — <span className="text-[#C25934] font-semibold">«Готовые конструкции»</span>
                  {' '}(6+ шаблонов на каждую категорию)
                  <br />или <span className="text-[#C25934] font-semibold">«Параметрический мастер»</span>
                  {' '}для создания по размерам
                </p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] text-[#5C5C5C] mono-text">
                  <span className="text-[#C25934]">←</span>
                  <span>смотрите панель слева</span>
                </div>
                <p className="text-[10px] text-[#5C5C5C]/60 mt-3">
                  Или перетащите деталь прямо сюда
                </p>
              </div>
            </div>
          )}

          {modules.map((mod) => {
            const isSelected = selectedId === mod.id;
            const hasConflict = collisions.has(mod.id);
            const isBody = BODY_MODULE_TYPES.includes(mod.type);
            const bbox = getModuleBBox(mod);
            const w = bbox.w * scale;
            const h = bbox.h * scale;
            const showLabel = w > 30 && h > 14;
            const rot = mod.rotation || 0;

            return (
              <div
                key={mod.id}
                className={`canvas-module ${isSelected ? 'selected' : ''} ${hasConflict ? 'conflict' : ''} ${isBody ? 'locked' : ''}`}
                style={{
                  left: mod.x * scale,
                  top: mod.y * scale,
                  width: w,
                  height: h,
                  backgroundColor: mod.material ? (mod.color_hex || '#C4A76C') : '#D0D0D0',
                  backgroundImage: mod.material
                    ? undefined
                    : 'repeating-linear-gradient(45deg, rgba(0,0,0,0.06) 0 4px, transparent 4px 10px)',
                  zIndex: getModuleZIndex(mod.type),
                  opacity: mod.material === 'glass' ? 0.5 : (mod.material ? 0.85 : 0.7),
                }}
                onMouseDown={(e) => handleMouseDown(e, mod.id)}
                onClick={(e) => e.stopPropagation()}
                onContextMenu={(e) => openCtx(e, mod.id)}
                data-testid={`canvas-module-${mod.id}`}
              >
                {showLabel && (
                  <div className="module-label flex flex-col items-center"
                    style={{ transform: rot ? `rotate(${-rot}deg)` : undefined }}>
                    <span>{mod.name_ru}</span>
                    {(w > 60 && h > 24) && (
                      <span className="text-[8px] opacity-70">{mod.width}x{mod.height}</span>
                    )}
                  </div>
                )}

                {isSelected && !BODY_MODULE_TYPES.includes(mod.type) && (
                  <>
                    {['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'].map(corner => (
                      <span
                        key={corner}
                        className={`resize-handle resize-${corner}`}
                        onMouseDown={(e) => handleResizeStart(e, mod.id, corner)}
                        data-testid={`resize-${mod.id}-${corner}`}
                      />
                    ))}
                  </>
                )}
              </div>
            );
          })}

          {/* Alignment guides */}
          {dragging && guides.map((g, i) => (
            g.axis === 'x' ? (
              <div key={`g-${i}`} className="guide-line"
                style={{ left: g.at * scale - 0.5, top: 0, bottom: 0, width: 1 }} />
            ) : (
              <div key={`g-${i}`} className="guide-line"
                style={{ top: g.at * scale - 0.5, left: 0, right: 0, height: 1 }} />
            )
          ))}

          {/* Dimension overlay */}
          {showDimensions && modules.map((mod) => {
            const bbox = getModuleBBox(mod);
            const x = bbox.x1 * scale;
            const y = bbox.y1 * scale;
            const w = bbox.w * scale;
            const h = bbox.h * scale;
            const isSelected = selectedId === mod.id;
            if (!isSelected && modules.length > 12) return null;
            return (
              <div key={`dim-${mod.id}`} className="pointer-events-none absolute" style={{ left: 0, top: 0 }}>
                <div className="dim-w" style={{ left: x, top: y - 18, width: w }}>
                  <span className="dim-text">{mod.width}</span>
                </div>
                <div className="dim-h" style={{ left: x - 28, top: y, height: h }}>
                  <span className="dim-text">{mod.height}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {ctx && (
        <ContextMenu
          x={ctx.x} y={ctx.y}
          onClose={() => setCtx(null)}
          onCopy={() => onCopyModule(ctx.moduleId)}
          onDelete={() => onDeleteModule(ctx.moduleId)}
          onRotate={() => onRotateModule(ctx.moduleId)}
        />
      )}
    </div>
  );
}
